import { createElement } from "react";
import type { RowData } from "@tanstack/react-table";
import type { DateOnlyRange } from "@design-system/DatePicker/DatePicker.types";
import type {
    DataGridPeopleMember,
    DataGridPersonValue,
    DataGridWorkspaceValue,
} from "../cells/cellValueTypes";
import { DateRangeCell } from "../cells/DateRangeCell";
import { PeopleCell } from "../cells/PeopleCell";
import { PersonCell } from "../cells/PersonCell";
import { TimestampCell } from "../cells/TimestampCell";
import { WorkspaceCell } from "../cells/WorkspaceCell";
import {
    cellOf,
    dataColumn,
    type Nullable,
    toAccessor,
} from "./columnFactories";
import { toAvatarDisplay } from "./columnSkeletons";
import type {
    DataGridPeopleColumnOptions,
    DataGridPersonColumnOptions,
    DataGridTimestampColumnOptions,
    DataGridWorkspaceColumnOptions,
} from "./kindOptions";
import { sortDateRange, sortPeople, sortText, sortTimestamp } from "./sortFns";
import type { DataGridColumnOptions, DataGridValueSource } from "./types";

const DEFAULT_PEOPLE_MAX = 4;

/**
 * Read-only kinds for list tables: people, workspaces, stacked avatars,
 * timestamps, and date ranges. Spread into `createColumns`.
 */
export const createListColumns = <TData extends RowData>() => ({
    person: (
        source: DataGridValueSource<TData, Nullable<DataGridPersonValue>>,
        options: DataGridPersonColumnOptions<TData>,
    ) =>
        dataColumn<TData>({
            kind: "person",
            ...toAccessor(source),
            options,
            sortFn: sortText,
            cell: (context, meta) =>
                createElement(PersonCell<TData>, {
                    context,
                    meta,
                    fallback: options.fallback,
                }),
        }),
    workspace: (
        source: DataGridValueSource<TData, Nullable<DataGridWorkspaceValue>>,
        options: DataGridWorkspaceColumnOptions<TData>,
    ) => {
        const { display, skeleton } = toAvatarDisplay(options.display);
        return dataColumn<TData>({
            kind: "workspace",
            ...toAccessor(source),
            options,
            sortFn: sortText,
            skeleton,
            cell: (context, meta) =>
                createElement(WorkspaceCell<TData>, { context, meta, display }),
        });
    },
    people: (
        source: DataGridValueSource<
            TData,
            Nullable<readonly DataGridPeopleMember[]>
        >,
        options: DataGridPeopleColumnOptions<TData>,
    ) =>
        dataColumn<TData>({
            kind: "people",
            ...toAccessor(source),
            options,
            sortFn: sortPeople,
            cell: (context, meta) =>
                createElement(PeopleCell<TData>, {
                    context,
                    meta,
                    max: options.max ?? DEFAULT_PEOPLE_MAX,
                }),
        }),
    /** ISO strings or `Date`s; shows the workspace day and sorts by instant. */
    timestamp: (
        source: DataGridValueSource<TData, Nullable<string | Date>>,
        options: DataGridTimestampColumnOptions<TData>,
    ) =>
        dataColumn<TData>({
            kind: "timestamp",
            ...toAccessor(source),
            options,
            sortFn: sortTimestamp,
            cell: (context, meta) =>
                createElement(TimestampCell<TData>, {
                    context,
                    meta,
                    timeZone: options.timeZone,
                }),
        }),
    dateRange: (
        source: DataGridValueSource<TData, Nullable<DateOnlyRange>>,
        options: DataGridColumnOptions<TData>,
    ) =>
        dataColumn<TData>({
            kind: "dateRange",
            ...toAccessor(source),
            options,
            sortFn: sortDateRange,
            cell: cellOf<TData>(DateRangeCell),
        }),
});
